import React, { useState, useMemo, useCallback } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard, useAnimateOnMount } from '../../common/AnimationUtils';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  TrendingUp,
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Grid3X3,
  Table,
  Info,
  ArrowUpDown,
  ClipboardList,
  Check
} from 'lucide-react';
import MissingDataHeatmap from '../charts/MissingnessHeatmap_Modern';
import MissingDataTimeline from '../charts/MissingDataTimeline_Modern';
import DetailedIssueTooltip from './DetailedIssueTooltip';  // NEW: Hover tooltip for row details
import IssueLogPanel from './IssueLogPanel';  // Inline issue log tab
import PulseBeacon from './PulseBeacon';  // Animated issue indicator
import CollapsibleSection from './CollapsibleSection';
import ScoreCard from '../charts/ScoreCard';
import EmptyStateCelebration from './EmptyStateCelebration';  // NEW: Time-series trend visualization
import './CompletenessSection.css';

// Hoisted to module scope: defining this inside the section component gave it a new
// identity on every re-render, which remounted every card and restarted the load animation.
const MetricCard = ({ colors, isDarkMode, onViewIssues, icon: Icon, title, value, subtitle, color, borderColor, detailedIssues = [], issueType = 'missing', totalCount = null, severity, animDelay = 0 }) => {
  const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
  const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
  const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');
  const hasIssues = detailedIssues.length > 0;

  const cardContent = (
    <div
      className="cps-metric-card"
      style={{
        position: 'relative',
        padding: '18px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
        overflow: 'hidden',
        cursor: hasIssues ? 'pointer' : 'default',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}
    >
      {/* Severity corner bracket — SVG L-shape that traces the 8px rounded corner */}
      {severity && (
        <svg
          width="30" height="30" viewBox="0 0 30 30"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={iconColor} strokeWidth="2" fill="none"
            strokeLinecap="round" opacity={0.9}
          />
        </svg>
      )}

      <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '10px',
          }}>
            {title}
            {hasIssues && (
              <Info size={11} style={{ color: '#2563EB', strokeWidth: 2 }} />
            )}
          </div>
          <div
            className="metric-value"
            style={{
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colors.text,
              lineHeight: 1.05,
              marginBottom: '6px',
            }}
          >
            <AnimatedNumber value={value} duration={800} />
          </div>
          <div style={{
            fontSize: '11.5px',
            fontWeight: 400,
            letterSpacing: '-0.005em',
            color: hasIssues ? '#2563EB' : colors.textMuted,
            lineHeight: 1.4,
          }}>
            {hasIssues ? 'View issue log →' : subtitle}
          </div>
        </div>
        {/* Phantom spacer — matches icon chip width so the text column is truly card-centered */}
        <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
  );

  // Wrap with click-to-tab if detailed issues are available
  if (hasIssues) {
    return (
      <AnimatedCard delay={animDelay} translateY={12}>
      <div onClick={() => onViewIssues && onViewIssues()}>
        {cardContent}
      </div>
      </AnimatedCard>
    );
  }

  return <AnimatedCard delay={animDelay} translateY={12}>{cardContent}</AnimatedCard>;
};

const CompletenessSection = ({ data, dataCollectorId, isSidePanelExpanded, isMobile = false }) => {

  const { isDarkMode } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('summary');
  
  // Collector Analysis view toggle (heatmap is default as per user request)
  const [collectorView, setCollectorView] = useState('heatmap');
  
  // Variable Analysis state
  const [variableSearch, setVariableSearch] = useState('');
  const [variableFilter, setVariableFilter] = useState('all');
  const [variableSortField, setVariableSortField] = useState('missingRate');
  const [variableSortDirection, setVariableSortDirection] = useState('desc');
  
  // Collector Table state
  const [collectorSortField, setCollectorSortField] = useState('avgMissingRate');
  const [collectorSortDirection, setCollectorSortDirection] = useState('desc');

  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    critical: '#DC2626',
    accent: isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  // Early return if no data
  if (!data?.overall) {
    return (
      <div className="completeness-empty-state" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '32px',
          borderRadius: '16px',
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <AlertTriangle size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Completeness Data Available
          </h3>
          <p style={{ fontSize: '13px', color: colors.textSecondary }}>
            Run a completeness check to see the analysis here.
          </p>
        </div>
      </div>
    );
  }

  // Helper function to get per_variable data (handles both old and new structure)
  const getPerVariableData = (dataObj) => {
    // New structure: data.overall.per_variable
    if (dataObj.per_variable) {
      return dataObj.per_variable;
    }
    // Old structure: data.overall directly contains variables (filter out metadata)
    const METADATA_KEYS = ['timeline_data', 'has_timeline', '_metadata', 'total_observations'];
    const filtered = {};
    Object.entries(dataObj).forEach(([key, value]) => {
      if (!METADATA_KEYS.includes(key) && 
          typeof value === 'object' && 
          value !== null && 
          'missing_percentage' in value) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  // Get per_variable data from overall
  const overallPerVariable = getPerVariableData(data.overall);
  
  // Get variables list
  const variables = Object.keys(overallPerVariable);
  
  // Extract detailed issues for tooltips (NEW)
  const detailedMissingIssues = useMemo(() => {
    // Check for aggregated detailed issues
    if (data.overall?._all_detailed_issues) {
      return data.overall._all_detailed_issues;
    }
    // Fallback: collect from per-variable data
    const allIssues = [];
    Object.entries(overallPerVariable).forEach(([variable, varData]) => {
      if (varData?.detailed_issues && Array.isArray(varData.detailed_issues)) {
        allIssues.push(...varData.detailed_issues);
      }
    });
    return allIssues.slice(0, 500); // Limit total
  }, [data.overall, overallPerVariable]);

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalVariables = variables.length;
    const avgMissingRate = totalVariables > 0 
      ? variables.reduce((sum, variable) => 
          sum + (overallPerVariable[variable]?.missing_percentage || 0), 0
        ) / totalVariables
      : 0;
    
    const variablesWithMissing = variables.filter(variable => 
      (overallPerVariable[variable]?.missing_percentage || 0) > 0
    ).length;
    
    const criticalVariables = variables.filter(variable => 
      (overallPerVariable[variable]?.missing_percentage || 0) > 20
    ).length;

    const completeVariables = variables.filter(variable => 
      (overallPerVariable[variable]?.missing_percentage || 0) === 0
    ).length;

    const partialVariables = variables.filter(variable => {
      const pct = overallPerVariable[variable]?.missing_percentage || 0;
      return pct > 0 && pct <= 20;
    }).length;

    const completenessScore = 100 - avgMissingRate;
    
    // Calculate total missing values across all variables
    const totalMissingValues = variables.reduce((sum, variable) => 
      sum + (overallPerVariable[variable]?.missing || 0), 0
    );

    // XLSForm skip logic stats
    const xlsformIntegrated = data.overall?._xlsform_integrated || false;
    let totalRawNulls = 0, totalTrueMissing = 0, totalCorrectlySkipped = 0, variablesWithSkipLogic = 0;
    
    if (xlsformIntegrated) {
      variables.forEach(variable => {
        const varData = overallPerVariable[variable];
        const skipAnalysis = varData?.skip_logic_analysis;
        if (skipAnalysis?.applied) {
          variablesWithSkipLogic++;
          totalRawNulls += skipAnalysis.raw_null_count || 0;
          totalTrueMissing += skipAnalysis.true_missing_count || 0;
          totalCorrectlySkipped += skipAnalysis.correctly_skipped_count || 0;
        } else {
          totalRawNulls += varData?.missing || 0;
          totalTrueMissing += varData?.missing || 0;
        }
      });
    }

    return {
      totalVariables,
      avgMissingRate,
      variablesWithMissing,
      criticalVariables,
      completeVariables,
      partialVariables,
      completenessScore,
      totalMissingValues,
      xlsformIntegrated,
      skipLogicStats: xlsformIntegrated ? {
        totalRawNulls, totalTrueMissing, totalCorrectlySkipped, variablesWithSkipLogic,
        savingsPercentage: totalRawNulls > 0 ? Math.round((totalCorrectlySkipped / totalRawNulls) * 100) : 0
      } : null
    };
  }, [data.overall, overallPerVariable, variables]);

  // Get quality level based on completeness score (matching AI Assessment methodology)
  const getQualityLevel = (score) => {
    if (score >= 95) return { label: 'Excellent', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' };
    if (score >= 85) return { label: 'Good', color: '#34D399', bgColor: 'rgba(52, 211, 153, 0.1)' };
    if (score >= 70) return { label: 'Moderate', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' };
    if (score >= 50) return { label: 'Poor', color: '#F97316', bgColor: 'rgba(249, 115, 22, 0.1)' };
    return { label: 'Critical', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
  };

  // Get severity color for missing rate
  const getSeverityColor = (missingRate) => {
    if (missingRate === 0) return colors.success;
    if (missingRate < 5) return '#34D399';
    if (missingRate < 15) return colors.warning;
    if (missingRate < 30) return '#F97316';
    return colors.error;
  };

  // Get status label
  const getStatusLabel = (missingRate) => {
    if (missingRate === 0) return 'Complete';
    if (missingRate < 5) return 'Good';
    if (missingRate < 15) return 'Fair';
    if (missingRate < 30) return 'Poor';
    return 'Critical';
  };

  // Prepare variable data for table
  const variableTableData = useMemo(() => {
    return variables.map(variable => {
      const varData = overallPerVariable[variable];
      return {
        name: variable,
        missingRate: varData?.missing_percentage || 0,
        missingCount: varData?.missing_count || 0,
        totalCount: varData?.total_count || 0,
        completeness: 100 - (varData?.missing_percentage || 0)
      };
    });
  }, [overallPerVariable, variables]);

  // Filter and sort variable data
  const filteredVariableData = useMemo(() => {
    let filtered = [...variableTableData];
    
    // Apply search filter
    if (variableSearch) {
      filtered = filtered.filter(v => 
        v.name.toLowerCase().includes(variableSearch.toLowerCase())
      );
    }
    
    // Apply status filter
    if (variableFilter !== 'all') {
      filtered = filtered.filter(v => {
        if (variableFilter === 'complete') return v.missingRate === 0;
        if (variableFilter === 'partial') return v.missingRate > 0 && v.missingRate <= 20;
        if (variableFilter === 'critical') return v.missingRate > 20;
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[variableSortField];
      let bVal = b[variableSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (variableSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [variableTableData, variableSearch, variableFilter, variableSortField, variableSortDirection]);

  // Prepare collector data for table
  const collectorTableData = useMemo(() => {
    if (!data.by_collector) return [];
    
    return Object.entries(data.by_collector).map(([collector, collectorData]) => {
      // Get per_variable data for this collector
      const collectorPerVariable = getPerVariableData(collectorData);
      const collectorVariables = Object.keys(collectorPerVariable);
      
      const avgMissingRate = collectorVariables.length > 0
        ? collectorVariables.reduce((sum, varName) => 
            sum + (collectorPerVariable[varName]?.missing_percentage || 0), 0
          ) / collectorVariables.length
        : 0;
      
      const variablesWithMissing = collectorVariables.filter(varName =>
        (collectorPerVariable[varName]?.missing_percentage || 0) > 0
      ).length;
      
      const totalVariables = collectorVariables.length;
      const totalObservations = collectorVariables.length > 0 
        ? collectorPerVariable[collectorVariables[0]]?.total_count || 0 
        : 0;

      return {
        collector,
        avgMissingRate,
        variablesWithMissing,
        totalVariables,
        totalObservations,
        completeness: 100 - avgMissingRate
      };
    });
  }, [data.by_collector]);

  // Sort collector data
  const sortedCollectorData = useMemo(() => {
    const sorted = [...collectorTableData];
    sorted.sort((a, b) => {
      let aVal = a[collectorSortField];
      let bVal = b[collectorSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (collectorSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [collectorTableData, collectorSortField, collectorSortDirection]);

  // Get heatmap data
  const heatmapData = useMemo(() => {
    if (!data.by_collector || !dataCollectorId) return [];
    
    return Object.entries(data.by_collector).flatMap(([collector, collectorData]) => {
      const collectorPerVariable = getPerVariableData(collectorData);
      return variables.map(variable => ({
        collector,
        variable,
        missingCount: collectorPerVariable[variable]?.missing_count || 0,
        missingPercentage: collectorPerVariable[variable]?.missing_percentage || 0,
        totalCount: collectorPerVariable[variable]?.total_count || 0
      }));
    });
  }, [data.by_collector, dataCollectorId, variables]);

  // Export to CSV
  const exportToCSV = useCallback((type) => {
    let csv = '';
    let filename = '';
    
    if (type === 'variables') {
      csv = 'Variable,Missing Rate (%),Missing Count,Total Count,Completeness (%),Status\n';
      filteredVariableData.forEach(v => {
        csv += `${v.name},${v.missingRate.toFixed(2)},${v.missingCount},${v.totalCount},${v.completeness.toFixed(2)},${getStatusLabel(v.missingRate)}\n`;
      });
      filename = `completeness_variables_${new Date().toISOString().split('T')[0]}.csv`;
    } else if (type === 'collectors') {
      csv = 'Collector,Avg Missing Rate (%),Variables Affected,Total Variables,Completeness (%),Rating\n';
      sortedCollectorData.forEach(c => {
        csv += `${c.collector},${c.avgMissingRate.toFixed(2)},${c.variablesWithMissing},${c.totalVariables},${c.completeness.toFixed(2)},${getStatusLabel(c.avgMissingRate)}\n`;
      });
      filename = `completeness_collectors_${new Date().toISOString().split('T')[0]}.csv`;
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredVariableData, sortedCollectorData]);

  // Handle sort toggle
  const handleVariableSort = (field) => {
    if (variableSortField === field) {
      setVariableSortDirection(variableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setVariableSortField(field);
      setVariableSortDirection('desc');
    }
  };

  const handleCollectorSort = (field) => {
    if (collectorSortField === field) {
      setCollectorSortDirection(collectorSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCollectorSortField(field);
      setCollectorSortDirection('desc');
    }
  };

  // Tab definitions
  const tabs = [
    { id: 'summary', label: 'Summary Dashboard', icon: BarChart3 },
    { id: 'variables', label: 'Variable Analysis', icon: Table },
    ...(dataCollectorId ? [{ id: 'collectors', label: 'Collector Analysis', icon: Grid3X3 }] : []),
    ...(detailedMissingIssues.length > 0 ? [{ id: 'issues', label: 'Issue Log', icon: ClipboardList }] : [])
  ];

  const qualityLevel = getQualityLevel(overallStats.completenessScore);

  // ============================================
  // RENDER COMPONENTS
  // ============================================

  // Tab Navigation
  // Metric Card Component

  // Quality Score Card (shared ScoreCard: large centred ring, text inside)
  const QualityScoreCard = () => {
    const score = overallStats.completenessScore;
    return (
      <ScoreCard
        className="cps-score-card"
        eyebrow="completeness score"
        score={score}
        label={qualityLevel.label}
        caption={score >= 95 ? 'Highly complete data'
          : score >= 85 ? 'Minor gaps detected'
          : score >= 70 ? 'Review missing patterns'
          : 'Address data gaps'}
        isDarkMode={isDarkMode}
        startDelay={620}
      />
    );
  };

  // Variable Breakdown Summary
  const VariableBreakdown = () => {
    const rows = [
      { label: 'Complete Variables', value: overallStats.completeVariables, Icon: Check, tone: 'success' },
      { label: 'Partial Missing', value: overallStats.partialVariables, Icon: AlertTriangle, tone: 'warning' },
      { label: 'Critical (>20%)', value: overallStats.criticalVariables, Icon: XCircle, tone: 'error' },
    ];
    const toneColor = (t) => t === 'success'
      ? (isDarkMode ? '#34d399' : '#047857')
      : t === 'warning'
      ? (isDarkMode ? '#fbbf24' : '#b45309')
      : (isDarkMode ? '#f87171' : '#b91c1c');

    const barsReady = useAnimateOnMount(400);

    return (
      <div
        className="cps-breakdown-card"
        style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}
      >
        <div style={{
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '0.16em',
          textTransform: 'lowercase',
          color: colors.textMuted,
          marginBottom: '14px',
        }}>
          variable breakdown
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {rows.map((r, idx) => {
            const c = toneColor(r.tone);
            const total = overallStats.totalVariables || 1;
            const pct = (r.value / total) * 100;
            return (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '4px',
                      background: isDarkMode
                        ? `linear-gradient(135deg, ${c}24, ${c}10)`
                        : `linear-gradient(135deg, ${c}18, ${c}08)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: c,
                    }}>
                      <r.Icon size={12} strokeWidth={2} />
                    </div>
                    <span style={{
                      fontSize: '12.5px',
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      color: colors.text,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {r.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, letterSpacing: '-0.01em', color: c }}>
                      {r.value}
                    </span>
                    <span style={{ fontSize: '11px', color: colors.textMuted }}>
                      / {overallStats.totalVariables}
                    </span>
                  </div>
                </div>
                {/* Thin progress bar, animated on mount */}
                <div style={{
                  width: '100%',
                  height: '3px',
                  background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '100px',
                  overflow: 'hidden',
                  marginLeft: '31px',
                  maxWidth: 'calc(100% - 31px)',
                }}>
                  <div
                    className="completion-bar-fill"
                    style={{
                      width: barsReady ? `${pct}%` : '0%',
                      height: '100%',
                      background: c,
                      borderRadius: '100px',
                      transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                      opacity: 0.75,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Top Issues Table (for Summary)
  const TopIssuesTable = ({ title, data, type }) => {
    const toneColor = (rate) => rate >= 20
      ? (isDarkMode ? '#f87171' : '#b91c1c')
      : rate >= 5
      ? (isDarkMode ? '#fbbf24' : '#b45309')
      : (isDarkMode ? '#34d399' : '#047857');

    return (
      <div
        className="cps-top-issues"
        style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '14px',
          gap: '10px',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '10px',
              fontWeight: 500,
              letterSpacing: '0.16em',
              textTransform: 'lowercase',
              color: colors.textMuted,
              marginBottom: '3px',
            }}>
              top issues
            </div>
            <div style={{
              fontSize: '13.5px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {title}
            </div>
          </div>
          <span style={{
            fontSize: '9.5px',
            fontWeight: 500,
            letterSpacing: '0.06em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            whiteSpace: 'nowrap',
            paddingTop: '14px',
          }}>
            top 5 by missing rate
          </span>
        </div>

        <div style={{ overflowX: 'auto', margin: '0 -4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '6px 10px 8px',
                  textAlign: 'left',
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  textTransform: 'lowercase',
                  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  {type === 'variables' ? 'variable' : 'collector'}
                </th>
                <th style={{
                  padding: '6px 10px 8px',
                  textAlign: 'right',
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  textTransform: 'lowercase',
                  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  missing rate
                </th>
                <th style={{
                  padding: '6px 10px 8px',
                  textAlign: 'right',
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  textTransform: 'lowercase',
                  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  status
                </th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 5).map((item, idx) => {
                const rate = type === 'variables' ? item.missingRate : item.avgMissingRate;
                const name = type === 'variables' ? item.name : item.collector;
                const c = toneColor(rate);
                return (
                  <tr key={idx} className="cps-row" style={{ transition: 'background 0.15s ease' }}>
                    <td style={{
                      padding: '9px 10px',
                      color: colors.text,
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }} title={name}>
                      {name}
                    </td>
                    <td style={{
                      padding: '9px 10px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      <span style={{ color: c, fontWeight: 500, fontSize: '13px' }}>
                        {rate.toFixed(1)}%
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 9px',
                        borderRadius: '100px',
                        fontSize: '10px',
                        fontWeight: 500,
                        letterSpacing: '0.04em',
                        textTransform: 'lowercase',
                        background: isDarkMode ? `${c}22` : `${c}14`,
                        color: c,
                        border: `0.5px solid ${c}33`,
                      }}>
                        {getStatusLabel(rate)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={3} style={{
                    padding: '22px',
                    textAlign: 'center',
                    color: colors.textMuted,
                    fontSize: '11.5px',
                    letterSpacing: '-0.005em',
                  }}>
                    No missing data detected
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Sortable Table Header
  const SortableHeader = ({ field, label, currentSort, currentDirection, onSort, align = 'left' }) => (
    <th 
      onClick={() => onSort(field)}
      style={{ 
        padding: '12px 16px', 
        textAlign: align, 
        color: colors.textSecondary, 
        fontWeight: '500',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        {currentSort === field && (
          currentDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        )}
        {currentSort !== field && <ArrowUpDown size={12} style={{ opacity: 0.4 }} />}
      </div>
    </th>
  );

  // Completion Bar
  const CompletionBar = ({ percentage, color }) => {
    const barReady = useAnimateOnMount(400);
    return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ 
        flex: 1, 
        height: '8px', 
        backgroundColor: colors.borderLight, 
        borderRadius: '4px',
        overflow: 'hidden',
        minWidth: '80px'
      }}>
        <div style={{
          width: barReady ? `${percentage}%` : '0%',
          height: '100%',
          backgroundColor: color,
          borderRadius: '4px',
          transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }} />
      </div>
      <span style={{ 
        color: colors.text, 
        fontSize: '13px',
        fontWeight: '500',
        minWidth: '50px',
        textAlign: 'right'
      }}>
        {barReady ? <AnimatedNumber value={percentage} decimals={1} suffix="%" delay={0} duration={800} /> : '0.0%'}
      </span>
    </div>
  ); };

  // ============================================
  // TAB CONTENT RENDERS
  // ============================================

  // Summary Dashboard Tab
  const renderSummaryDashboard = () => (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'minmax(0, 1fr)',
      width: '100%',
      gap: '16px',
      marginTop: '80px'
    }}>
      
      {/* Skip Logic Banner - Shows when XLSForm with relevance is used AND values were correctly skipped */}
      {overallStats.xlsformIntegrated && overallStats.skipLogicStats?.totalCorrectlySkipped > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px',
          backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
          border: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
          borderRadius: '12px'
        }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '10px',
            backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <CheckCircle size={22} style={{ color: '#10B981' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#10B981' }}>XLSForm Skip Logic Applied</span>
              <span style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
                borderRadius: '10px', color: '#10B981', fontWeight: '500' }}>
                {overallStats.skipLogicStats.variablesWithSkipLogic} variables
              </span>
            </div>
            <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: 0, lineHeight: '1.5' }}>
              <strong style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>
                {overallStats.skipLogicStats.totalCorrectlySkipped.toLocaleString()}
              </strong> empty values are correctly skipped (not missing). 
              Only <strong style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>
                {overallStats.skipLogicStats.totalTrueMissing.toLocaleString()}
              </strong> are truly missing.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '20px', paddingLeft: '20px',
            borderLeft: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}` }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#10B981' }}>
                {overallStats.skipLogicStats.savingsPercentage}%
              </div>
              <div style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Correctly Skipped</div>
            </div>
          </div>
        </div>
      )}

      {/* XLSForm Loaded But No Skip Logic Applied Banner - Transparency indicator */}
      {overallStats.xlsformIntegrated && 
       (!overallStats.skipLogicStats?.totalCorrectlySkipped || overallStats.skipLogicStats.totalCorrectlySkipped === 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px',
          backgroundColor: isDarkMode ? 'rgba(107, 114, 128, 0.1)' : 'rgba(107, 114, 128, 0.08)',
          border: `1px solid ${isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(107, 114, 128, 0.2)'}`,
          borderRadius: '12px'
        }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px',
            backgroundColor: isDarkMode ? 'rgba(107, 114, 128, 0.2)' : 'rgba(107, 114, 128, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <Info size={20} style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                XLSForm Loaded - Standard Analysis Used
              </span>
            </div>
            <p style={{ fontSize: '12px', color: isDarkMode ? '#6B7280' : '#9CA3AF', margin: 0, lineHeight: '1.4' }}>
              {overallStats.skipLogicStats?.variablesWithSkipLogic === 0 
                ? 'None of the analyzed variables have skip logic (relevance) conditions in the XLSForm.'
                : 'Skip logic was evaluated but no values qualified as "correctly skipped" - all missing values are truly missing.'}
            </p>
          </div>
          <div style={{ 
            padding: '6px 12px', 
            backgroundColor: isDarkMode ? 'rgba(107, 114, 128, 0.15)' : 'rgba(107, 114, 128, 0.1)',
            borderRadius: '8px',
            fontSize: '11px',
            color: isDarkMode ? '#9CA3AF' : '#6B7280',
            fontWeight: '500'
          }}>
            Using IQR Analysis
          </div>
        </div>
      )}

      <CollapsibleSection 
        title="Key Metrics" 
        isDarkMode={isDarkMode} 
        colors={colors}
        badge={
          overallStats.criticalVariables > 0 ? (
            <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
              {overallStats.criticalVariables} critical
            </span>
          ) : null
        }
      >
      {/* Top Metrics Row */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px' 
      }}>
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={0}
          icon={BarChart3}
          title="Variables Analyzed"
          value={overallStats.totalVariables}
          subtitle="Checked for completeness"
          color="#6B7280"
          borderColor="#6B7280"
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={80}
          icon={AlertTriangle}
          title="Variables with Missing"
          value={overallStats.variablesWithMissing}
          subtitle="Have missing values"
          color={overallStats.variablesWithMissing > 0 ? colors.warning : colors.success}
          borderColor={overallStats.variablesWithMissing > 0 ? colors.warning : colors.success}
          severity={overallStats.variablesWithMissing > 0 ? 'warning' : null}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={160}
          icon={XCircle}
          title="Critical Variables"
          value={overallStats.criticalVariables}
          subtitle=">20% missing rate"
          color={overallStats.criticalVariables > 0 ? colors.error : colors.success}
          borderColor={overallStats.criticalVariables > 0 ? colors.error : colors.success}
          severity={overallStats.criticalVariables > 0 ? 'critical' : null}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={240}
          icon={TrendingUp}
          title="Avg Missing Rate"
          value={`${overallStats.avgMissingRate.toFixed(1)}%`}
          subtitle="Across all variables"
          color={getSeverityColor(overallStats.avgMissingRate)}
          borderColor={getSeverityColor(overallStats.avgMissingRate)}
          detailedIssues={detailedMissingIssues}
          issueType="missing"
          totalCount={overallStats.totalMissingValues}
          severity={overallStats.avgMissingRate > 20 ? 'critical' : overallStats.avgMissingRate > 5 ? 'warning' : null}
        />
      </div>
      </CollapsibleSection>

      <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
      {/* Compact Analysis Row: Score + Breakdown + Top Issues */}
      <div className="analysis-grid-4col" style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
        gap: '16px' 
      }}>
        <AnimatedCard delay={320} translateY={12}><QualityScoreCard /></AnimatedCard>
        <AnimatedCard delay={400} translateY={12}><VariableBreakdown /></AnimatedCard>
        <AnimatedCard delay={480} translateY={12}>
        <TopIssuesTable 
          title="Top Variable Issues" 
          data={[...variableTableData].sort((a, b) => b.missingRate - a.missingRate).filter(v => v.missingRate > 0)}
          type="variables"
        />
        </AnimatedCard>
        <AnimatedCard delay={560} translateY={12}>
        {dataCollectorId && collectorTableData.length > 0 ? (
          <TopIssuesTable 
            title="Top Collector Issues" 
            data={[...collectorTableData].sort((a, b) => b.avgMissingRate - a.avgMissingRate).filter(c => c.avgMissingRate > 0)}
            type="collectors"
          />
        ) : (
          <div style={{
            backgroundColor: colors.cardBg, borderRadius: '12px',
            border: `1px solid ${colors.border}`, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '18px',
            color: colors.textMuted, fontSize: '13px', textAlign: 'center'
          }}>No collector ID specified</div>
        )}
        </AnimatedCard>
      </div>

      <style>{`
        @media (max-width: 1200px) { .analysis-grid-4col { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 768px) { .analysis-grid-4col { grid-template-columns: 1fr !important; } }
      `}</style>
      </CollapsibleSection>
        
      {/* Timeline (if available) */}
      {data.overall.has_timeline && data.overall.timeline_data && data.overall.timeline_data.length > 0 && (
        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '18px'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
            Missing Data Timeline
          </h3>
          <MissingDataTimeline data={data.overall.timeline_data} />
        </div>
      )}

    </div>
  );

  // Variable Analysis Tab
  const renderVariableAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* Toolbar */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Left: Search and Filter */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: colors.textMuted
            }} />
            <input
              type="text"
              placeholder="Search variables..."
              value={variableSearch}
              onChange={(e) => setVariableSearch(e.target.value)}
              style={{
                padding: '10px 12px 10px 36px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '14px',
                width: '220px',
                outline: 'none'
              }}
            />
          </div>

          {/* Filter Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} style={{ color: colors.textSecondary }} />
            <select
              value={variableFilter}
              onChange={(e) => setVariableFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '14px',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">All Variables ({variableTableData.length})</option>
              <option value="complete">Complete ({overallStats.completeVariables})</option>
              <option value="partial">Partial ({overallStats.partialVariables})</option>
              <option value="critical">Critical ({overallStats.criticalVariables})</option>
            </select>
          </div>
        </div>

        {/* Right: Export */}
        <button
          onClick={() => exportToCSV('variables')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.cardBg,
            color: colors.text,
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      </AnimatedCard>

      {/* Results Count */}
      <AnimatedCard delay={80} translateY={12}>
      <div style={{ 
        fontSize: '13px', 
        color: colors.textMuted, 
        marginBottom: '12px' 
      }}>
        Showing {filteredVariableData.length} of {variableTableData.length} variables
      </div>
      </AnimatedCard>

      {/* Table */}
      <AnimatedCard delay={160} translateY={12}>
      <div style={{
        backgroundColor: colors.cardBg,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: colors.cardBgAlt }}>
                <SortableHeader 
                  field="name" 
                  label="Variable" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                />
                <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500', minWidth: '180px' }}>
                  Completeness
                </th>
                <SortableHeader 
                  field="missingRate" 
                  label="Missing Rate" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                  align="center"
                />
                <SortableHeader 
                  field="missingCount" 
                  label="Missing Count" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                  align="center"
                />
                <SortableHeader 
                  field="totalCount" 
                  label="Total Count" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                  align="center"
                />
                <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredVariableData.map((variable, idx) => (
                <tr key={variable.name} style={{ 
                  borderTop: `1px solid ${colors.borderLight}`,
                  backgroundColor: idx % 2 === 0 ? 'transparent' : colors.cardBgAlt
                }}>
                  <td style={{ 
                    padding: '14px 16px', 
                    color: colors.text,
                    fontWeight: '500',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }} title={variable.name}>
                    {variable.name}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <CompletionBar 
                      percentage={variable.completeness} 
                      color={getSeverityColor(variable.missingRate)}
                    />
                  </td>
                  <td style={{ 
                    padding: '14px 16px', 
                    textAlign: 'center',
                    color: getSeverityColor(variable.missingRate),
                    fontWeight: '600'
                  }}>
                    {variable.missingRate.toFixed(1)}%
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.text }}>
                    {variable.missingCount.toLocaleString()}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.textSecondary }}>
                    {variable.totalCount.toLocaleString()}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: `${getSeverityColor(variable.missingRate)}15`,
                      color: getSeverityColor(variable.missingRate)
                    }}>
                      {getStatusLabel(variable.missingRate)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredVariableData.length === 0 && (
          <div style={{ 
            padding: '32px', 
            textAlign: 'center', 
            color: colors.textSecondary 
          }}>
            <Info size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p>No variables match your search criteria</p>
          </div>
        )}
      </div>
      </AnimatedCard>
    </div>
  );

  // Collector Analysis Tab
  const renderCollectorAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* View Toggle */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '14px',
        paddingBottom: '10px',
        borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '3px',
          }}>
            collector analysis
          </div>
          <h3 style={{
            fontSize: '15px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: colors.text,
            margin: 0,
          }}>
            Missing-data patterns by data collector
          </h3>
        </div>

        <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
          {[
            { key: 'heatmap', icon: Grid3X3, label: 'heatmap' },
            { key: 'table',   icon: Table,   label: 'table' },
          ].map(({ key, icon: VIcon, label }) => {
            const isActive = collectorView === key;
            return (
              <button
                key={key}
                onClick={() => setCollectorView(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '0',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isActive ? colors.text : colors.textMuted,
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'lowercase',
                  cursor: 'pointer',
                  opacity: isActive ? 1 : 0.7,
                  borderBottom: isActive ? '2px solid #D97706' : '2px solid transparent',
                  marginBottom: '-11px',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
              >
                <VIcon size={13} strokeWidth={isActive ? 2 : 1.75} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
      </AnimatedCard>

      {/* Export row — shown only for table view, below the toggle */}
      {collectorView === 'table' && (
        <AnimatedCard delay={40} translateY={8}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button
            onClick={() => exportToCSV('collectors')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              padding: '7px 14px',
              borderRadius: '100px',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
              color: colors.text,
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              fontFamily: "'Outfit', -apple-system, sans-serif",
              transition: 'all 0.2s ease',
            }}
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
        </AnimatedCard>
      )}

      {/* Heatmap View */}
      <AnimatedCard delay={80} translateY={12}>
      {collectorView === 'heatmap' && (
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          padding: '18px',
        }}>
          {heatmapData.length > 0 ? (
            <MissingDataHeatmap 
              data={heatmapData}
              height={Math.max(400, Object.keys(data.by_collector || {}).length * 50)}
            />
          ) : (
            <div style={{ 
              padding: '32px', 
              textAlign: 'center', 
              color: colors.textSecondary 
            }}>
              <Info size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No collector-level heatmap data available</p>
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Table View */}
      <AnimatedCard delay={160} translateY={12}>
      {collectorView === 'table' && (
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: colors.cardBgAlt }}>
                  <SortableHeader 
                    field="collector" 
                    label="Data Collector" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                  />
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500', minWidth: '180px' }}>
                    Completeness
                  </th>
                  <SortableHeader 
                    field="avgMissingRate" 
                    label="Avg Missing Rate" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                    align="center"
                  />
                  <SortableHeader 
                    field="variablesWithMissing" 
                    label="Variables Affected" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                    align="center"
                  />
                  <SortableHeader 
                    field="totalObservations" 
                    label="Observations" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                    align="center"
                  />
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500' }}>
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectorData.map((collector, idx) => (
                  <tr key={collector.collector} style={{ 
                    borderTop: `1px solid ${colors.borderLight}`,
                    backgroundColor: idx % 2 === 0 ? 'transparent' : colors.cardBgAlt
                  }}>
                    <td style={{ 
                      padding: '14px 16px', 
                      color: colors.text,
                      fontWeight: '500',
                      fontFamily: 'monospace'
                    }}>
                      {collector.collector}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <CompletionBar 
                        percentage={collector.completeness} 
                        color={getSeverityColor(collector.avgMissingRate)}
                      />
                    </td>
                    <td style={{ 
                      padding: '14px 16px', 
                      textAlign: 'center',
                      color: getSeverityColor(collector.avgMissingRate),
                      fontWeight: '600'
                    }}>
                      {collector.avgMissingRate.toFixed(1)}%
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.text }}>
                      {collector.variablesWithMissing} / {collector.totalVariables}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.textSecondary }}>
                      {collector.totalObservations.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: `${getSeverityColor(collector.avgMissingRate)}15`,
                        color: getSeverityColor(collector.avgMissingRate)
                      }}>
                        {getStatusLabel(collector.avgMissingRate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedCollectorData.length === 0 && (
            <div style={{ 
              padding: '32px', 
              textAlign: 'center', 
              color: colors.textSecondary 
            }}>
              <Info size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No collector data available</p>
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Rating Legend */}
      <AnimatedCard delay={320} translateY={12}>
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
        flexWrap: 'wrap',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Info size={13} style={{ color: colors.textMuted, opacity: 0.7 }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
          }}>
            rating thresholds
          </span>
        </div>
        {[
          { label: 'Complete', threshold: '0%',   color: isDarkMode ? '#34d399' : '#047857' },
          { label: 'Good',     threshold: '<5%',  color: isDarkMode ? '#10b981' : '#059669' },
          { label: 'Fair',     threshold: '<15%', color: isDarkMode ? '#fbbf24' : '#b45309' },
          { label: 'Poor',     threshold: '<30%', color: '#D97706' },
          { label: 'Critical', threshold: '≥30%', color: isDarkMode ? '#f87171' : '#b91c1c' },
        ].map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '3px',
              backgroundColor: item.color,
              boxShadow: `0 0 6px ${item.color}33`,
            }} />
            <span style={{
              fontSize: '11.5px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: colors.text,
            }}>
              {item.label}
            </span>
            <span style={{
              fontSize: '10.5px',
              fontWeight: 400,
              color: colors.textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {item.threshold}
            </span>
          </div>
        ))}
      </div>
      </AnimatedCard>
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div 
      className="completeness-section activeQualityDetector"
      style={{
        width: isSidePanelExpanded ? '100%' : '100%',
        maxWidth: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
        overflow: isMobile ? 'visible' : 'hidden',
      }}
    >
      <DimensionHeader
        slug="completeness"
        title="Completeness"
        description="Flags missing values and partially filled records across your dataset. Higher completeness means cleaner downstream analysis and fewer skipped questions to chase."
        rememberCollapseKey="completeness"
        stat={{
          score: overallStats.completenessScore,
          scoreLabel: 'completeness score',
          unit: '%',
        }}
        tabs={tabs.map(t => {
          if (t.id === 'variables')  return { ...t, count: overallStats.totalVariables, countTone: 'neutral' };
          if (t.id === 'collectors') return { ...t, count: Object.keys(data?.by_collector || {}).length, countTone: 'neutral' };
          if (t.id === 'issues')     return { ...t, count: detailedMissingIssues.length,
                                              countTone: detailedMissingIssues.length > 0 ? 'alert' : 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />
    <div style={{ 
          minHeight: '600px',
          marginTop: '16px'
        }}>
      {activeTab === 'summary' && renderSummaryDashboard()}
      {activeTab === 'variables' && renderVariableAnalysis()}
      {activeTab === 'collectors' && dataCollectorId && renderCollectorAnalysis()}

      {activeTab === 'issues' && (
        <AnimatedCard delay={0} translateY={12}>
        <IssueLogPanel
          issues={detailedMissingIssues}
          title={`${data.overall?.total_missing?.toLocaleString() || detailedMissingIssues.length} Missing Values`}
          issueType="missing"
          totalCount={data.overall?.total_missing || detailedMissingIssues.length}
        />
        </AnimatedCard>
      )}
      </div>
    </div>
  );
};

export default CompletenessSection;